import { useState, useRef, useCallback, useEffect } from 'react'
import {
  PSFTP_SERVICE_UUID, openPsFtpOn, recStatusOn, recStartOn, recStopOn,
  fetchLatestExerciseOn, SAMPLE_HR,
} from './usePolarOffline'
import { getBluetoothErrorMessage, backgroundKeeper } from '../utils/mobileResilience'

const IN_NATIVE_SHELL = typeof window !== 'undefined'
  && !!window.ReactNativeWebView
  && (window.__MANABI_SHELL__ === true || window.__VITALCORE_SHELL__ === true)
const postToShell = (obj) => { try { window.ReactNativeWebView.postMessage(JSON.stringify(obj)) } catch {} }

const ECG_LIVE_MAX = 130 * 3600

function pushEcg(ref, samples) {
  ref.current.push(...samples)
  if (ref.current.length > ECG_LIVE_MAX + 13000) ref.current = ref.current.slice(-ECG_LIVE_MAX)
}

const HR_SERVICE  = 0x180D
const HR_CHAR     = 0x2A37
const PMD_SERVICE = 'fb005c80-02e7-f387-1cad-8acd2d8df0c8'
const PMD_CONTROL = 'fb005c81-02e7-f387-1cad-8acd2d8df0c8'
const PMD_DATA    = 'fb005c82-02e7-f387-1cad-8acd2d8df0c8'

const ECG_START = Uint8Array.from([
  0x02, 0x00,
  0x00, 0x01, 0x82, 0x00,
  0x01, 0x01, 0x0E, 0x00,
])

const ACC_START = Uint8Array.from([
  0x02, 0x02,
  0x00, 0x01, 0x32, 0x00,
  0x01, 0x01, 0x10, 0x00,
  0x02, 0x01, 0x08, 0x00,
])

function parseHR(dataView) {
  const flags = dataView.getUint8(0)
  const is16bit = flags & 0x1
  const contactSupported = (flags >> 2) & 0x1
  const contactDetected  = (flags >> 1) & 0x1
  const hasRR   = (flags >> 4) & 0x1
  let offset = 1
  const hr = is16bit ? dataView.getUint16(offset, true) : dataView.getUint8(offset)
  offset += is16bit ? 2 : 1
  const rr = []
  if (hasRR) {
    while (offset + 1 < dataView.byteLength) {
      rr.push(Math.round((dataView.getUint16(offset, true) / 1024) * 1000))
      offset += 2
    }
  }
  return { hr, rr, contactSupported: Boolean(contactSupported), contactDetected: Boolean(contactDetected) }
}

function parseECG(dataView) {
  if (dataView.byteLength < 11 || dataView.getUint8(0) !== 0x00) return null
  const frameType = dataView.getUint8(9)
  if (frameType !== 0) return null
  const samples = []
  for (let i = 10; i + 2 < dataView.byteLength; i += 3) {
    let v = dataView.getUint8(i) | (dataView.getUint8(i + 1) << 8) | (dataView.getUint8(i + 2) << 16)
    if (v >= 0x800000) v -= 0x1000000
    samples.push(v)
  }
  return samples.length ? samples : null
}

function parseACC(bytes) {
  if (bytes.length < 11 || bytes[0] !== 0x02) return null
  const ft = bytes[9]
  const compressed = (ft & 0x80) !== 0
  const type = ft & 0x7f
  if (type > 2) return null
  const bytesPer = type + 1, CH = 3
  const toSigned = (v, bits) => v >= 2 ** (bits - 1) ? v - 2 ** bits : v
  const readSample = (off) => {
    let v = 0
    for (let b = 0; b < bytesPer; b++) v += bytes[off + b] * 2 ** (8 * b)
    return toSigned(v, 8 * bytesPer)
  }
  const factor = 8000 / 2 ** (8 * bytesPer - 1)
  const out = [[], [], []]
  if (!compressed) {
    for (let off = 10; off + CH * bytesPer <= bytes.length; off += CH * bytesPer)
      for (let c = 0; c < CH; c++) out[c].push(readSample(off + c * bytesPer) * factor)
  } else {
    let off = 10
    if (off + CH * bytesPer > bytes.length) return null
    const ref = []
    for (let c = 0; c < CH; c++) { ref.push(readSample(off)); off += bytesPer }
    for (let c = 0; c < CH; c++) out[c].push(ref[c] * factor)
    while (off + 2 <= bytes.length) {
      const deltaBits = bytes[off], n = bytes[off + 1]
      off += 2
      if (!deltaBits || !n) break
      const nBytes = Math.ceil(deltaBits * n * CH / 8)
      if (off + nBytes > bytes.length) break
      let bitPos = 0
      const getDelta = () => {
        let v = 0
        for (let b = 0; b < deltaBits; b++) v |= ((bytes[off + ((bitPos + b) >> 3)] >> ((bitPos + b) & 7)) & 1) << b
        bitPos += deltaBits
        return toSigned(v, deltaBits)
      }
      for (let s = 0; s < n; s++) for (let c = 0; c < CH; c++) { ref[c] += getDelta(); out[c].push(ref[c] * factor) }
      off += nBytes
    }
  }
  return out[0].length ? { x: out[0], y: out[1], z: out[2] } : null
}

export function makeStepCounter(fs = 50) {
  const aF = 1 - Math.exp(-1 / (0.07 * fs)), aS = 1 - Math.exp(-1 / (1.1 * fs))
  const THR = 14
  const RELEASE = 6
  const MIN_STEP_SAMPLES = Math.round(0.32 * fs) // 320 ms min (cadencia maxima 187 pasos/min)
  const MAX_STEP_SAMPLES = Math.round(1.25 * fs) // 1250 ms max (cadencia minima 48 pasos/min)
  const BOUT_CONFIRMATION = 4 // Requiere racha de 4 zancadas ritmicas para confirmar marcha y filtrar baile/balanceo estatico

  let emaFast = null, emaSlow = null, steps = 0, t = 0
  let lastCross = -1e9
  let armed = true
  let durAbove = 0
  let prevX = null, prevM = null
  let maxVertJerk = 0
  let pendingBout = 0

  return {
    push(x, y, z) {
      t++
      const m = Math.sqrt(x * x + y * y + z * z)
      // Vector de impacto vertical de talon a lo largo del eje vertical del esternon
      const vertJerk = prevX != null ? Math.abs(x - prevX) : 0
      prevX = x
      prevM = m

      emaSlow = emaSlow == null ? m : emaSlow + aS * (m - emaSlow)
      emaFast = emaFast == null ? m : emaFast + aF * (m - emaFast)
      const band = emaFast - emaSlow

      if (band > THR) {
        durAbove++
        if (vertJerk > maxVertJerk) maxVertJerk = vertJerk

        const dt = t - lastCross
        // Validacion biomecanica de impacto vertical de talón + cadencia fisiológica humana
        if (armed && durAbove >= 2 && maxVertJerk >= 4.0 && dt >= MIN_STEP_SAMPLES) {
          if (dt <= MAX_STEP_SAMPLES) {
            // Zancadas consecutivas en cadencia ritmica
            pendingBout++
            if (pendingBout >= BOUT_CONFIRMATION) {
              if (pendingBout === BOUT_CONFIRMATION) {
                // Confirmamos el tren de pasos acumulado
                steps += BOUT_CONFIRMATION
              } else {
                steps += 1
              }
            }
          } else {
            // Se rompio el ritmo o fue un balanceo/sacudida aislada: iniciar nuevo tren
            pendingBout = 1
          }
          lastCross = t
          armed = false
        }
      } else if (!armed && band < RELEASE) {
        armed = true
        durAbove = 0
        maxVertJerk = 0
      } else if (band < RELEASE) {
        durAbove = 0
        maxVertJerk = 0
      }

      // Si transcurre mas de 1.25 s sin otra zancada ritmica, descartar candidatos no confirmados
      if (pendingBout > 0 && pendingBout < BOUT_CONFIRMATION && t - lastCross > MAX_STEP_SAMPLES) {
        pendingBout = 0
      }
    },
    count() { return steps },
    reset() {
      steps = 0
      pendingBout = 0
      lastCross = -1e9
      armed = true
      emaFast = emaSlow = null
      t = 0
      durAbove = 0
      maxVertJerk = 0
      prevX = null
      prevM = null
    },
  }
}

// Conexión reactiva a anuncios BLE (evita error "Bluetooth device is no longer in range" en Chrome)
function connectViaAdvertisements(device, timeoutMs = 8000) {
  return new Promise(async (resolve, reject) => {
    const abortController = new AbortController()
    let isConnected = false

    const timer = setTimeout(() => {
      if (!isConnected) {
        try { abortController.abort() } catch (_) {}
        reject(new Error(`timeout_advertisement_${device.name}`))
      }
    }, timeoutMs)

    const onAdvertisementReceived = async (event) => {
      console.log(`[usePolarH10] ¡Polar detectado en rango! RSSI: ${event.rssi} dBm`)
      try { abortController.abort() } catch (_) {}
      clearTimeout(timer)
      device.removeEventListener('advertisementreceived', onAdvertisementReceived)

      try {
        isConnected = true
        console.log('[usePolarH10] Conectando GATT tras recibir anuncio...')
        const server = await device.gatt.connect()
        resolve(server)
      } catch (err) {
        reject(err)
      }
    }

    device.addEventListener('advertisementreceived', onAdvertisementReceived)

    try {
      if (typeof device.watchAdvertisements === 'function') {
        await device.watchAdvertisements({ signal: abortController.signal })
        console.log('[usePolarH10] Escuchando anuncios BLE de Polar en segundo plano...')
      } else {
        const server = await device.gatt.connect()
        clearTimeout(timer)
        resolve(server)
      }
    } catch (err) {
      clearTimeout(timer)
      device.removeEventListener('advertisementreceived', onAdvertisementReceived)
      try {
        const server = await device.gatt.connect()
        resolve(server)
      } catch (directErr) {
        reject(directErr)
      }
    }
  })
}

export function usePolarH10() {
  const [status, setStatus] = useState('disconnected')
  const [hr, setHr] = useState(null)
  const [hrHistory, setHrHistory] = useState([])
  const [deviceName, setDeviceName] = useState(null)
  const [error, setError] = useState(null)
  const [ecgAvailable, setEcgAvailable] = useState(false)
  const [ecgTick, setEcgTick] = useState(0)
  const [steps, setSteps] = useState(0)
  const [battery, setBattery] = useState(null)
  const [skinContact, setSkinContact] = useState(true)
  const [deviceId, setDeviceId] = useState(() => { try { return localStorage.getItem('vc_polar_id') } catch (_) { return null } })
  const deviceRef = useRef(null), serverRef = useRef(null), charRef = useRef(null), hrListenerRef = useRef(null), ecgRef = useRef([])
  const accRef = useRef({ x: [], y: [], z: [], startTime: null }), stepCounterRef = useRef(null), reconnectTimer = useRef(null), reconnectCount = useRef(0), intentionalDisc = useRef(false)
  const backfillRef = useRef({ ps: null, startedAt: null }), gapStartRef = useRef(null), sessionStartTimeRef = useRef(null), lastHrRef = useRef(null), heartbeatWatchdogRef = useRef(null)

  const ANCHOR_KEY = 'vc_backfill_anchor'
  const readAnchor = () => { try { return JSON.parse(localStorage.getItem(ANCHOR_KEY) || 'null') } catch { return null } }
  const writeAnchor = (v) => { try { v ? localStorage.setItem(ANCHOR_KEY, JSON.stringify(v)) : localStorage.removeItem(ANCHOR_KEY) } catch {} }
  const [sensorImport, setSensorImport] = useState(null)
  const clearSensorImport = useCallback(() => setSensorImport(null), [])

  const syncSensorMemory = useCallback(async (server, deviceName) => {
    const allowSync = new URLSearchParams(window.location.search).has('sync_mem')
    if (!IN_NATIVE_SHELL && !allowSync) return
    backfillRef.current = { ps: null, startedAt: null }
    try {
      await Promise.race([
        (async () => {
          const ps = await openPsFtpOn(server)
          const st = await recStatusOn(ps)
          const anchor = readAnchor()
          if (st.on && !anchor?.startedAt) { backfillRef.current = { ps, startedAt: null }; return }
          if (st.on) { await recStopOn(ps); await new Promise(r => setTimeout(r, 400)) }
          if (anchor?.startedAt) {
            try {
              let lastEnd = 0
              try { lastEnd = Number(localStorage.getItem('vc_last_session_end')) || 0 } catch {}
              const cut = Math.max(anchor.startedAt, lastEnd + 2000)
              const fill = (await fetchLatestExerciseOn(ps, anchor.startedAt)).filter(r => r.hr > 20 && r.hr < 250 && r.time > cut && r.time < Date.now() - 2000).map(r => ({ time: r.time, hr: r.hr, rr: r.rr ?? [] }))
              if (fill.length >= 60) setSensorImport({ readings: fill, from: fill[0].time, to: fill[fill.length - 1].time, deviceName })
            } catch (e) { console.warn('No se pudo traer lo grabado por el sensor:', e?.message) }
          }
          writeAnchor(null); await recStartOn(ps, SAMPLE_HR); const startedAt = Date.now(); writeAnchor({ startedAt }); backfillRef.current = { ps, startedAt }
        })(),
        new Promise((_, rej) => setTimeout(() => rej(new Error('timeout_psftp')), 3000))
      ])
    } catch (e) { console.warn('Memoria del sensor no disponible o omitida:', e?.message) }
  }, [])

  const backfillGap = useCallback(async (server) => {
    const gapStart = gapStartRef.current, anchor = backfillRef.current.startedAt, gapEnd = Date.now()
    gapStartRef.current = null
    if (!gapStart || !anchor || gapEnd - gapStart < 15000) return
    const ps = await openPsFtpOn(server); backfillRef.current.ps = ps
    try {
      const st = await recStatusOn(ps)
      if (st.on) { await recStopOn(ps); await new Promise(r => setTimeout(r, 500)) }
      const fill = (await fetchLatestExerciseOn(ps, anchor)).filter(r => r.hr > 20 && r.hr < 250 && r.time > gapStart + 1500 && r.time < gapEnd - 1500).map(r => ({ time: r.time, hr: r.hr, rr: r.rr ?? [] }))
      if (fill.length) setHrHistory(prev => [...prev, ...fill].sort((a, b) => a.time - b.time))
    } finally {
      try { await recStartOn(ps, SAMPLE_HR); const startedAt = Date.now(); writeAnchor({ startedAt }); backfillRef.current = { ps, startedAt } } catch (e) { console.warn('No se pudo rearmar el respaldo:', e?.message) }
    }
  }, [])

  const subscribeBattery = useCallback(async (server) => {
    try {
      const service = await server.getPrimaryService('battery_service'), char = await service.getCharacteristic('battery_level'), val = await char.readValue()
      setBattery(val.getUint8(0)); char.addEventListener('characteristicvaluechanged', (e) => { try { setBattery(e.target.value.getUint8(0)) } catch {} }); await char.startNotifications().catch(() => {})
    } catch (e) { console.log('[usePolarH10] Servicio de batería no disponible:', e?.message) }
  }, [])

  const resolveDeviceId = useCallback(async (server, device) => {
    let resolvedId = null
    try {
      const service = await server.getPrimaryService('device_information'), char = await service.getCharacteristic('serial_number_string'), val = await char.readValue()
      const decoded = new TextDecoder('utf-8').decode(val).trim(); if (decoded) resolvedId = decoded
    } catch {}
    if (!resolvedId && device?.name) {
      const match = device.name.match(/Polar\s+(?:H10\s+)?([A-Z0-9]+)/i)
      if (match) resolvedId = match[1]; else resolvedId = device.name.replace(/Polar\s+/i, '').trim()
    }
    if (resolvedId) {
      setDeviceId(resolvedId); try { localStorage.setItem('vc_polar_id', resolvedId) } catch {}
      const token = localStorage.getItem('polar_token')
      if (token) {
        const API_BASE = import.meta.env.VITE_API_URL || '/api'
        fetch(`${API_BASE}/user/device`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ deviceId: resolvedId, deviceName: device?.name || 'Polar H10' }) }).catch(() => {})
      }
    }
  }, [])

  const handlePmdFrame = useCallback((dataView) => {
    const tag = dataView.getUint8(0)
    if (tag === 0x00) {
      const s = parseECG(dataView); if (!s) return; pushEcg(ecgRef, s); setEcgTick(t => t + 1)
    } else if (tag === 0x02) {
      const acc = parseACC(new Uint8Array(dataView.buffer, dataView.byteOffset, dataView.byteLength)); if (!acc) return
      if (!accRef.current.startTime) accRef.current.startTime = Date.now()
      for (let i = 0; i < acc.x.length; i++) { accRef.current.x.push(acc.x[i]); accRef.current.y.push(acc.y[i]); accRef.current.z.push(acc.z[i]) }
      const c = stepCounterRef.current || (stepCounterRef.current = makeStepCounter(50))
      for (let i = 0; i < acc.x.length; i++) c.push(acc.x[i], acc.y[i], acc.z[i]); setSteps(c.count())
    }
  }, [])

  useEffect(() => {
    if (!IN_NATIVE_SHELL) return
    const onNative = (e) => {
      const m = e.detail; if (!m) return
      if (m.kind === 'status') { setStatus(m.status); if (m.deviceName !== undefined) setDeviceName(m.deviceName); if (m.error !== undefined) setError(m.error); if (m.status === 'disconnected' || m.status === 'reconnecting') setHr(null); if (m.status === 'disconnected') setEcgAvailable(false) }
      else if (m.kind === 'hr') { setHr(m.hr); setHrHistory(prev => [...prev, { time: m.time ?? Date.now(), hr: m.hr, rr: m.rr ?? [] }]); setStatus('connected') }
      else if (m.kind === 'hr-batch' && Array.isArray(m.readings) && m.readings.length) {
        const mapped = m.readings.filter(r => r && r.hr).map(r => ({ time: r.time ?? Date.now(), hr: r.hr, rr: r.rr ?? [] }))
        if (mapped.length) { setHrHistory(prev => { const seen = new Set(prev.map(x => x.time)); const novel = mapped.filter(x => !seen.has(x.time)); return novel.length ? [...prev, ...novel].sort((a, b) => a.time - b.time) : prev }); setHr(mapped[mapped.length - 1].hr); setStatus('connected') }
      } else if (m.kind === 'ecg' && m.samples?.length) { pushEcg(ecgRef, m.samples); setEcgTick(t => t + 1); setEcgAvailable(true) }
      else if (m.kind === 'steps') { setSteps(m.steps ?? 0) }
      else if (m.kind === 'battery') { setBattery(m.battery) }
      else if (m.kind === 'device-id') { setDeviceId(m.deviceId) }
    }
    window.addEventListener('vc-native', onNative); return () => window.removeEventListener('vc-native', onNative)
  }, [])

  useEffect(() => {
    const handleUnload = () => { if (deviceRef.current?.gatt?.connected) { try { deviceRef.current.gatt.disconnect() } catch (e) { console.warn('Error desconectando H10 al descargar página:', e) } } }
    window.addEventListener('beforeunload', handleUnload); window.addEventListener('pagehide', handleUnload)
    return () => {
      window.removeEventListener('beforeunload', handleUnload); window.removeEventListener('pagehide', handleUnload)
      if (deviceRef.current?.gatt?.connected) { try { deviceRef.current.gatt.disconnect() } catch (e) { console.warn('Error desconectando H10 al desmontar hook:', e) } }
    }
  }, [])

  const subscribeHR = useCallback(async (server) => {
    const service = await server.getPrimaryService(HR_SERVICE), char = await service.getCharacteristic(HR_CHAR); charRef.current = char
    if (hrListenerRef.current) { try { char.removeEventListener('characteristicvaluechanged', hrListenerRef.current) } catch {} }
    const onHr = (e) => {
      const { hr, rr, contactSupported, contactDetected } = parseHR(e.target.value)
      if (contactSupported && !contactDetected) { setSkinContact(false); setHr(null); return }
      setSkinContact(true); if (!hr || hr < 32 || hr > 240) return; setHr(hr); lastHrRef.current = hr; if (!sessionStartTimeRef.current) sessionStartTimeRef.current = Date.now()
      clearTimeout(heartbeatWatchdogRef.current); heartbeatWatchdogRef.current = setTimeout(() => { setHr(null) }, 3500)
      setHrHistory(prev => { const now = Date.now(); if (prev.length > 0 && Math.abs(now - prev[prev.length - 1].time) < 400) return prev; return [...prev, { time: now, hr, rr }] }); setStatus('connected')
    }
    hrListenerRef.current = onHr; char.addEventListener('characteristicvaluechanged', onHr); await char.startNotifications(); setStatus('connected'); setError(null)
  }, [])

  const scheduleReconnect = useCallback(() => {
    if (intentionalDisc.current || !deviceRef.current) return
    reconnectCount.current++; if (reconnectCount.current > 2400) { setStatus('disconnected'); reconnectCount.current = 0; return }
    reconnectTimer.current = setTimeout(async () => {
      if (intentionalDisc.current || !deviceRef.current) return
      try {
        let server = deviceRef.current.gatt
        if (!server || !server.connected) server = await Promise.race([deviceRef.current.gatt.connect(), new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 8000))])
        serverRef.current = server; await subscribeHR(server); subscribeBattery(server).catch(() => {}); resolveDeviceId(server, deviceRef.current).catch(() => {})
        try {
          const pmd = await server.getPrimaryService(PMD_SERVICE), pmdCtrl = await pmd.getCharacteristic(PMD_CONTROL), pmdData = await pmd.getCharacteristic(PMD_DATA)
          pmdData.addEventListener('characteristicvaluechanged', (e) => handlePmdFrame(e.target.value)); await pmdData.startNotifications(); await pmdCtrl.startNotifications()
          try { await pmdCtrl.writeValueWithResponse(ECG_START); setEcgAvailable(true) } catch {}
          await new Promise(r => setTimeout(r, 300)); try { await pmdCtrl.writeValueWithResponse(ACC_START) } catch {}
        } catch {}
        reconnectCount.current = 0; setStatus('connected'); setError(null); backgroundKeeper.start(); await new Promise(r => setTimeout(r, 400)); try { await backfillGap(server) } catch (e) { console.warn('Relleno del hueco falló:', e?.message) }
      } catch (err) { console.warn('Reintento de conexión BLE:', err?.message); scheduleReconnect() }
    }, 2000)
  }, [subscribeHR, handlePmdFrame, backfillGap])

  const handleDisconnect = useCallback(() => {
    backgroundKeeper.stop(); if (intentionalDisc.current) return
    if (deviceRef.current?.gatt?.connected) { console.log('[usePolarH10] Desconexión espuria ignorada: GATT continúa conectado.'); setStatus('connected'); return }
    if (gapStartRef.current == null) gapStartRef.current = Date.now(); clearTimeout(heartbeatWatchdogRef.current); setHr(null); setEcgAvailable(false); setStatus('reconnecting'); setError('El sensor se desconectó. Verificá el contacto con la piel y la batería.')
    try {
      const sessionTimeSec = sessionStartTimeRef.current ? Math.round((Date.now() - sessionStartTimeRef.current) / 1000) : 0
      const token = localStorage.getItem('polar_token')
      if (token) {
        const API_BASE = import.meta.env.VITE_API_URL || '/api'
        fetch(`${API_BASE}/sensor/disconnect-log`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ deviceName: deviceRef.current?.name ?? 'Polar H10', reason: 'Corte inesperado de enlace Bluetooth GATT', details: { sessionTimeSec, lastHr: lastHrRef.current, reconnectAttempts: reconnectCount.current, userAgent: navigator.userAgent }, sessionTimeSec, reconnectCount: reconnectCount.current }) }).catch(() => {})
      }
    } catch {}
    reconnectCount.current = 0; clearTimeout(reconnectTimer.current); scheduleReconnect()
  }, [])

  const connect = useCallback(async (opts = {}) => {
    const isAuto = opts?.isAuto === true
    clearTimeout(reconnectTimer.current); intentionalDisc.current = false; reconnectCount.current = 0
    setStatus('connecting'); setError(null); setEcgAvailable(false); setSteps(0); setBattery(null)
    if (IN_NATIVE_SHELL) { postToShell({ type: 'ble:connect' }); return }
    const bleErr = getBluetoothErrorMessage(); if (bleErr) { if (!isAuto) setError(bleErr); setStatus('disconnected'); return }

    let device = null
    let usedPermitted = false
    const bleId = (() => { try { return localStorage.getItem('vc_ble_device_id') } catch (_) { return null } })()
    const savedPolarId = (() => { try { return localStorage.getItem('vc_polar_id') } catch (_) { return null } })()
    const savedPolarName = (() => { try { return localStorage.getItem('vc_polar_name') } catch (_) { return null } })()

    if (navigator.bluetooth && navigator.bluetooth.getDevices) {
      try {
        const permitted = await navigator.bluetooth.getDevices()
        if (bleId) {
          device = permitted.find(d => d.id === bleId)
        }
        if (!device && savedPolarId) {
          device = permitted.find(d => d.name && d.name.includes(savedPolarId))
        }
        if (!device && savedPolarName) {
          device = permitted.find(d => d.name === savedPolarName)
        }
        if (!device) {
          device = permitted.find(d => d.name && d.name.startsWith('Polar'))
        }
        if (device) {
          console.log('[usePolarH10] Dispositivo vinculado detectado:', device.name, device.id)
          usedPermitted = true
        }
      } catch (e) {
        console.warn('[usePolarH10] Error leyendo dispositivos vinculados:', e?.message)
      }
    }

    try {
      if (!device) {
        if (isAuto) {
          // Si es auto-conexión al inicio, no abrir diálogo manual (requiere gesto de usuario)
          console.log('[usePolarH10] No hay Polar vinculado en este navegador. Esperando click manual.')
          setStatus('disconnected')
          return
        }
        device = await navigator.bluetooth.requestDevice({
          filters: [ { namePrefix: 'Polar' }, { services: [HR_SERVICE] } ],
          optionalServices: [HR_SERVICE, PMD_SERVICE, 'battery_service', 'device_information'],
        })
      }

      deviceRef.current = device; setDeviceName(device.name ?? 'Polar H10')
      try {
        if (device.id) localStorage.setItem('vc_ble_device_id', device.id)
        if (device.name) localStorage.setItem('vc_polar_name', device.name)
      } catch (_) {}
      device.removeEventListener('gattserverdisconnected', handleDisconnect)
      try { if (device.gatt?.connected) device.gatt.disconnect() } catch {}
      await new Promise(r => setTimeout(r, 250)); // Pausa estratégica

      let server = device.gatt
      if (!server || !server.connected) {
        try {
          if (usedPermitted && 'watchAdvertisements' in device) {
            console.log('[usePolarH10] Intentando handshake reactivo con watchAdvertisements...', device.name)
            server = await connectViaAdvertisements(device, 7000)
          } else {
            server = await Promise.race([
              device.gatt.connect(),
              new Promise((_, rej) => setTimeout(() => rej(new Error('timeout_direct')), 8000)),
            ])
          }
        } catch (directErr) {
          if (usedPermitted && !isAuto) {
            console.log('[usePolarH10] Falló auto-conexión a Polar guardado, abriendo selector manual...', directErr.message)
            device = await navigator.bluetooth.requestDevice({
              filters: [ { namePrefix: 'Polar' }, { services: [HR_SERVICE] } ],
              optionalServices: [HR_SERVICE, PMD_SERVICE, 'battery_service', 'device_information'],
            })
            deviceRef.current = device; setDeviceName(device.name ?? 'Polar H10')
            try { if (device.name) localStorage.setItem('vc_polar_name', device.name) } catch (_) {}
            try { if (device.gatt?.connected) device.gatt.disconnect() } catch {}
            await new Promise(r => setTimeout(r, 250)); // Pausa estratégica
            server = await Promise.race([
              device.gatt.connect(),
              new Promise((_, rej) => setTimeout(() => rej(new Error('timeout_manual')), 10000)),
            ])
          } else {
            throw directErr
          }
        }
      }

      serverRef.current = server; gapStartRef.current = null
      device.addEventListener('gattserverdisconnected', handleDisconnect)
      await subscribeHR(server); subscribeBattery(server).catch(() => {})
      resolveDeviceId(server, device).catch(() => {})
      setStatus('connected'); setError(null); backgroundKeeper.start()

      try {
        const pmd = await server.getPrimaryService(PMD_SERVICE)
        const pmdCtrl = await pmd.getCharacteristic(PMD_CONTROL)
        const pmdData = await pmd.getCharacteristic(PMD_DATA)
        pmdData.addEventListener('characteristicvaluechanged', (e) => handlePmdFrame(e.target.value))
        await pmdData.startNotifications(); await pmdCtrl.startNotifications()
        try { await pmdCtrl.writeValueWithResponse(ECG_START); setEcgAvailable(true) } catch (e) { console.warn('ECG no disponible:', e.message) }
        await new Promise(r => setTimeout(r, 200)); try { await pmdCtrl.writeValueWithResponse(ACC_START) } catch (e) { console.warn('Acelerómetro no disponible:', e.message) }
      } catch (pmdErr) { console.warn('PMD no disponible:', pmdErr?.message) }
    } catch (err) {
      const msg = err.message ?? ''
      if (!isAuto && !msg.toLowerCase().includes('cancel')) setError(`Error de conexión: ${msg}`)
      setStatus('disconnected')
    }
  }, [handleDisconnect, resolveDeviceId, subscribeBattery, subscribeHR, handlePmdFrame])

  const disconnect = useCallback(async () => {
    backgroundKeeper.stop(); intentionalDisc.current = true; sessionStartTimeRef.current = null; lastHrRef.current = null; clearTimeout(heartbeatWatchdogRef.current); clearTimeout(reconnectTimer.current)
    try {
      const token = localStorage.getItem('polar_token')
      if (token) { const API_BASE = import.meta.env.VITE_API_URL || '/api'; fetch(`${API_BASE}/live/update`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ hr: null, status: 'offline', stopped: true }) }).catch(() => {}) }
    } catch {}
    if (IN_NATIVE_SHELL) { postToShell({ type: 'ble:disconnect' }); setStatus('disconnected'); setHr(null); setEcgAvailable(false); return }
    if (deviceRef.current) {
      deviceRef.current.removeEventListener('gattserverdisconnected', handleDisconnect)
      if (deviceRef.current.gatt.connected) {
        if (backfillRef.current.startedAt) {
          try {
            const ps = backfillRef.current.ps ?? await openPsFtpOn(serverRef.current), t4 = () => new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 4000))
            await Promise.race([recStopOn(ps), t4()]).catch(() => {}); await new Promise(r => setTimeout(r, 400)); await Promise.race([recStartOn(ps, 1), t4()]); writeAnchor({ startedAt: Date.now() })
          } catch (e) { writeAnchor(null); console.warn('El sensor no quedó grabando:', e?.message) }
        }
        try { if (charRef.current) await charRef.current.stopNotifications() } catch {}
        deviceRef.current.gatt.disconnect()
      }
    }
    deviceRef.current = null; backfillRef.current = { ps: null, startedAt: null }; setStatus('disconnected'); setHr(null); setEcgAvailable(false)
  }, [handleDisconnect])

  const clearHistory = useCallback(() => { setHrHistory([]); stepCounterRef.current?.reset?.(); stepCounterRef.current = null; accRef.current = { x: [], y: [], z: [], startTime: null }; setSteps(0) }, [])

  useEffect(() => {
    if (hr !== null && status !== 'connected') { setStatus('connected'); setError(null) }
    else if (status === 'reconnecting' && deviceRef.current?.gatt?.connected) { console.log('[usePolarH10] Auto-sanación: sensor conectado a nivel GATT. Restaurando connected.'); setStatus('connected'); setError(null) }
  }, [hr, status])

  // ── Auto-conexión silenciosa al abrir la app (si el Polar ya fue vinculado antes) ──
  useEffect(() => {
    if (IN_NATIVE_SHELL) return
    if (typeof navigator === 'undefined' || !navigator.bluetooth?.getDevices) return
    if (status !== 'disconnected' || intentionalDisc.current) return
    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled) return
      try {
        const permitted = await navigator.bluetooth.getDevices()
        if (cancelled) return
        const bleId = (() => { try { return localStorage.getItem('vc_ble_device_id') } catch (_) { return null } })()
        const savedId = (() => { try { return localStorage.getItem('vc_polar_id') || '18930C3E' } catch (_) { return '18930C3E' } })()
        const savedName = (() => { try { return localStorage.getItem('vc_polar_name') || 'Polar H10 18930C3E' } catch (_) { return 'Polar H10 18930C3E' } })()
        let polar = null
        if (bleId) polar = permitted.find(d => d.id === bleId)
        if (!polar && savedId) polar = permitted.find(d => d.name && d.name.includes(savedId))
        if (!polar && savedName) polar = permitted.find(d => d.name === savedName)
        if (!polar) polar = permitted.find(d => d.name && d.name.startsWith('Polar'))

        if (polar && (!deviceRef.current || !deviceRef.current.gatt?.connected)) {
          console.log('[usePolarH10] Dispositivo Polar vinculado detectado al inicio. Auto-conectando silenciosamente...', polar.name)
          connect({ isAuto: true })
        }
      } catch (e) {
        console.log('[usePolarH10] Auto-connect en inicio no disponible:', e?.message)
      }
    }, 400)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [status, connect])

  // ── Disparo alternativo en primera interacción del usuario (para navegadores con política estricta de activación) ──
  useEffect(() => {
    if (typeof window === 'undefined' || !navigator?.bluetooth?.getDevices) return
    const tryAutoOnFirstInteraction = async () => {
      window.removeEventListener('pointerdown', tryAutoOnFirstInteraction)
      window.removeEventListener('keydown', tryAutoOnFirstInteraction)
      if (deviceRef.current?.gatt?.connected || status === 'connected' || intentionalDisc.current) return
      try {
        const permitted = await navigator.bluetooth.getDevices()
        const bleId = (() => { try { return localStorage.getItem('vc_ble_device_id') } catch (_) { return null } })()
        const savedId = (() => { try { return localStorage.getItem('vc_polar_id') || '18930C3E' } catch (_) { return '18930C3E' } })()
        const savedName = (() => { try { return localStorage.getItem('vc_polar_name') || 'Polar H10 18930C3E' } catch (_) { return 'Polar H10 18930C3E' } })()
        let polar = null
        if (bleId) polar = permitted.find(d => d.id === bleId)
        if (!polar && savedId) polar = permitted.find(d => d.name && d.name.includes(savedId))
        if (!polar && savedName) polar = permitted.find(d => d.name === savedName)
        if (!polar) polar = permitted.find(d => d.name && d.name.startsWith('Polar'))

        if (polar && (!deviceRef.current || !deviceRef.current.gatt?.connected)) {
          console.log('[usePolarH10] Primera interacción de usuario: conectando con Polar guardado...', polar.name)
          connect({ isAuto: true })
        }
      } catch (_) {}
    }
    window.addEventListener('pointerdown', tryAutoOnFirstInteraction, { once: true, passive: true })
    window.addEventListener('keydown', tryAutoOnFirstInteraction, { once: true, passive: true })
    return () => {
      window.removeEventListener('pointerdown', tryAutoOnFirstInteraction)
      window.removeEventListener('keydown', tryAutoOnFirstInteraction)
    }
  }, [status, connect])

  return { status, hr, hrHistory, deviceName, error, battery, deviceId, skinContact, connect, disconnect, clearHistory, ecgSamples: ecgRef, ecgTick, ecgAvailable, accSamples: accRef, steps, sensorImport, clearSensorImport }
}
